import { useEffect, useRef } from "react";
import * as THREE from "three";
import { sampleAccentGradient } from "../../lib/accentGradient";

const PLANE_WIDTH = 14;
const PLANE_DEPTH = 9;
const WIDTH_SEGMENTS = 46;
const DEPTH_SEGMENTS = 28;

function terrainHeight(x: number, y: number): number {
  return (
    Math.sin(x * 0.55 + y * 0.35 + 0.4) * 0.9 +
    Math.sin(x * 0.32 - y * 0.5 + 2.1) * 0.63 +
    Math.sin(x * 1.1 + y * 0.15 - 1.0) * 0.22
  );
}

function buildTerrain(): THREE.Group {
  const geometry = new THREE.PlaneGeometry(
    PLANE_WIDTH,
    PLANE_DEPTH,
    WIDTH_SEGMENTS,
    DEPTH_SEGMENTS
  ).toNonIndexed();

  const position = geometry.attributes.position as THREE.BufferAttribute;
  const colors = new Float32Array(position.count * 3);
  let maxHeight = 0;

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const y = position.getY(i);
    const height = terrainHeight(x, y);
    position.setZ(i, height);
    maxHeight = Math.max(maxHeight, Math.abs(height));
  }

  for (let i = 0; i < position.count; i++) {
    const x = position.getX(i);
    const height = position.getZ(i);
    const t = (x + PLANE_WIDTH / 2) / PLANE_WIDTH;
    const brightness = 0.7 + (height / (maxHeight || 1)) * 0.4;
    const color = sampleAccentGradient(t).multiplyScalar(brightness);
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }

  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.computeVertexNormals();

  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    flatShading: true,
    roughness: 0.55,
    metalness: 0.15,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.rotation.x = -Math.PI / 2;

  const wireframe = new THREE.LineSegments(
    new THREE.WireframeGeometry(geometry),
    new THREE.LineBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0.12 })
  );
  wireframe.rotation.x = -Math.PI / 2;

  const group = new THREE.Group();
  group.add(mesh, wireframe);
  return group;
}

const PIN_FRACTIONS = [0.22, 0.52, 0.8] as const;

function buildPins(): THREE.Group {
  const group = new THREE.Group();
  for (const fraction of PIN_FRACTIONS) {
    const x = (fraction - 0.5) * PLANE_WIDTH;
    const z = 1.4;
    const surfaceY = terrainHeight(x, -z);

    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.015, 0.015, 1.6, 6),
      new THREE.MeshBasicMaterial({ color: 0x4f6a8a })
    );
    post.position.set(x, surfaceY + 0.8, z);

    const cap = new THREE.Mesh(
      new THREE.BoxGeometry(0.14, 0.14, 0.14),
      new THREE.MeshStandardMaterial({
        color: 0x08111d,
        emissive: 0x16c9f4,
        emissiveIntensity: 0.6,
        roughness: 0.4,
      })
    );
    cap.position.set(x, surfaceY + 1.6, z);

    group.add(post, cap);
  }
  return group;
}

export interface LowPolyTerrainProps {
  className?: string;
  title?: string;
}

/**
 * Experimental 3D replacement for NetworkField: a low-poly faceted terrain
 * rendered with three.js instead of animated SVG paths. Camera parallax
 * (rather than vertex animation) is what a flat SVG can't fake, so that's
 * the whole demo here — the mesh itself is static once built.
 */
export function LowPolyTerrain({
  className,
  title = "Woven network activity",
}: LowPolyTerrainProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x020812, 0.055);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    const baseCameraPosition = new THREE.Vector3(0, 3.2, 6.5);
    camera.position.copy(baseCameraPosition);
    camera.lookAt(0, 0.3, -1.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const ambient = new THREE.AmbientLight(0x223355, 0.9);
    const keyLight = new THREE.DirectionalLight(0x16c9f4, 0.8);
    keyLight.position.set(5, 6, 4);
    const rimLight = new THREE.DirectionalLight(0x7457ff, 0.6);
    rimLight.position.set(-4, 3, -3);
    scene.add(ambient, keyLight, rimLight);

    const sceneGroup = new THREE.Group();
    sceneGroup.position.set(-1, -1.5, -3.5);
    sceneGroup.scale.setScalar(1.25);
    const terrain = buildTerrain();
    const pins = buildPins();
    sceneGroup.add(terrain, pins);
    scene.add(sceneGroup);

    function resize() {
      const { clientWidth, clientHeight } = container as HTMLDivElement;
      if (clientWidth === 0 || clientHeight === 0) {
        return;
      }
      camera.aspect = clientWidth / clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(clientWidth, clientHeight);
      renderer.render(scene, camera);
    }

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    let rafId = 0;
    if (!reduceMotion) {
      const clock = new THREE.Clock();
      const animate = () => {
        const t = clock.getElapsedTime();
        camera.position.set(
          baseCameraPosition.x + Math.sin(t * 0.15) * 1.1,
          baseCameraPosition.y + Math.sin(t * 0.22) * 0.25,
          baseCameraPosition.z
        );
        camera.lookAt(0, 0.3, -1.5);
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(animate);
      };
      rafId = requestAnimationFrame(animate);
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      container.removeChild(renderer.domElement);
      renderer.dispose();
      terrain.traverse((child) => {
        if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
          child.geometry.dispose();
          const material = child.material as THREE.Material | THREE.Material[];
          if (Array.isArray(material)) {
            material.forEach((m) => m.dispose());
          } else {
            material.dispose();
          }
        }
      });
      pins.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          (child.material as THREE.Material).dispose();
        }
      });
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={className ? `low-poly-terrain ${className}` : "low-poly-terrain"}
      role="img"
      aria-label={title}
    />
  );
}
